import { createContext, useContext, useState, useCallback } from 'react';

const LanguageContext = createContext();

// English translations
const en = {
  // Navigation
  nav: {
    home: 'Home',
    about: 'About Us',
    services: 'Services',
    training: 'Training Programs',
    govEdu: 'Government & Education',
    careers: 'Careers',
    contact: 'Contact Us'
  },
  
  // Header
  header: {
    officialWebsite: 'Official Website of Imagenators Global Technology Pvt. Ltd.',
    textSize: 'Text Size',
    language: 'Language'
  },
  
  // Home Page
 home: {
  // Hero
  tagline: 'Technology • Innovation • Growth',
  heroTitle: 'Smart Digital Solutions for a Connected World',
  heroSubtitle:
    'We design scalable websites, AI-driven platforms, cloud systems and enterprise solutions for startups, businesses, institutions and growing organizations.',
  exploreServices: 'Explore Our Solutions',
  contactUs: 'Contact Us',
  learnMore: 'Learn More',

  // Introduction
  introTitle: 'Your Trusted Technology Partner',
  introText:
    'Imagenators Global Technology Private Limited (IGT) is a technology-driven company delivering modern digital solutions, AI-powered systems and professional training programs. We help startups, enterprises, educational institutions and organizations transform ideas into powerful digital products.',

  // Services Overview
  servicesTitle: 'What We Build',
  servicesSubtitle:
    'End-to-end digital solutions designed for performance, scalability and growth',

  // Training
  trainingTitle: 'Training & Skill Development',
  trainingSubtitle:
    'Industry-ready skills for students, professionals and institutions',
  trainingDescription:
    'Our training programs focus on practical, hands-on learning in high-demand technologies like Artificial Intelligence, Full-Stack Development, Cloud Computing and Data Science.',
  viewPrograms: 'View Training Programs',

  // Partners
  partnersTitle: 'Who We Work With',
  partnersSubtitle:
    'Startups, enterprises, institutions and organizations that trust us',

  // Why Choose
  whyTitle: 'Why Choose IGT?',
  whySubtitle:
    'A perfect blend of technology expertise, innovation and reliability',

  // CTA
  ctaTitle: 'Let’s Build Something Powerful Together',
  ctaSubtitle:
    'From idea to execution, we help you create digital solutions that make an impact.',
  ctaButton: 'Start Your Project'
},

  
  // About Page
  about: {
    pageTitle: 'About Us',
    breadcrumb: 'About IGT',
    
    overviewTitle: 'Organization Overview',
    overviewText1: 'Imagenators Global Technology Private Limited (IGT) is an emerging technology company incorporated under the Companies Act, 2013, with CIN: U62099UP2025PTC223917. We are committed to delivering innovative IT solutions and professional development services.',
    overviewText2: 'Our focus is on empowering government departments, educational institutions, and enterprises with cutting-edge technology solutions that drive efficiency, transparency, and growth.',
    
    missionTitle: 'Our Mission',
    missionText: 'To enable digital transformation across government, education, and enterprise sectors through innovative technology solutions, quality training programs, and dedicated service excellence.',
    
    visionTitle: 'Our Vision',
    visionText: 'To become a leading technology partner for institutional digital transformation in India, recognized for innovation, reliability, and impactful solutions that benefit society.',
    
    valuesTitle: 'Our Core Values',
    values: {
      integrity: 'Integrity & Transparency',
      innovation: 'Innovation & Excellence',
      commitment: 'Client Commitment',
      quality: 'Quality Assurance'
    }
  },
  
  // Services Page
  services: {
    pageTitle: 'Our Services',
    breadcrumb: 'Services',
    intro: 'IGT offers a comprehensive range of IT services designed to meet the unique needs of government, education, and enterprise sectors.',
    
    itConsulting: 'IT Services & Consulting',
    itConsultingDesc: 'Strategic IT consulting and advisory services to help organizations optimize their technology investments and digital initiatives.',
    
    software: 'Software & Application Development',
    softwareDesc: 'Custom software development, web applications, and mobile solutions tailored to your specific requirements.',
    
    cloud: 'Cloud & DevOps Solutions',
    cloudDesc: 'Cloud migration, infrastructure management, and DevOps implementation for scalable and efficient operations.',
    
    digitalMarketing: 'Digital Marketing Solutions',
    digitalMarketingDesc: 'Comprehensive digital marketing services including SEO, social media management, and online presence optimization.',
    
    ai: 'Artificial Intelligence Solutions',
    aiDesc: 'AI-powered solutions including machine learning, natural language processing, and intelligent automation.',
    
    erp: 'ERP Solutions & Implementation',
    erpDesc: 'Enterprise Resource Planning solutions for streamlined operations and integrated business management.',
    
    govEnterprise: 'Government & Enterprise IT',
    govEnterpriseDesc: 'Specialized IT solutions for government departments and large enterprises with focus on security and compliance.'
  },
  
training: {
  pageTitle: 'Training & Programs',
  breadcrumb: 'Workshops',

  intro: 'IGT conducts structured offline workshops designed to provide hands-on technology exposure, innovation-driven learning, and practical skill development for schools, colleges, and institutions.',

  robotics: 'Robotics & Innovation Workshop',
  roboticsDesc: 'A structured hands-on robotics workshop where students assemble, program, and test functional robotic models using graded learning kits (Basic, Intermediate, and Advanced levels).',

  aiTraining: 'AI & Emerging Technology Workshop',
  aiTrainingDesc: 'An interactive workshop introducing students to Artificial Intelligence concepts, real-world applications, and guided demonstrations.',

  ml: 'Data Science Foundations Workshop',
  mlDesc: 'Hands-on introduction to data visualization, analytical thinking, and foundational machine learning concepts.',

  python: 'Python Programming Workshop',
  pythonDesc: 'Beginner-friendly coding workshop covering programming fundamentals, logical thinking, and basic application development.',

  fullstack: 'Web Development Workshop',
  fullstackDesc: 'Practical introduction to website development, frontend basics, and simple deployment concepts.',

  cloudTraining: 'Cloud & Modern Infrastructure Workshop',
  cloudTrainingDesc: 'Overview of cloud platforms, infrastructure basics, and modern deployment practices through guided sessions.',

  devops: 'DevOps & Automation Workshop',
  devopsDesc: 'Introduction to version control, CI/CD concepts, and automation fundamentals with practical demonstrations.',

  duration: 'Duration',
  days: 'Days',

  knowMore: 'Know More',

  highlights: 'Workshop Highlights',
  highlight1: 'Hands-on practical implementation',
  highlight2: 'Interactive offline sessions',
  highlight3: 'Workshop participation certificate',
  highlight4: 'Structured robotics kit-based learning'
},
  
  // Government & Education Page
  govEdu: {
    pageTitle: 'Government & Education Solutions',
    breadcrumb: 'Government & Education',
    intro: 'Specialized technology solutions designed for government departments and educational institutions.',
    
    govTitle: 'Government IT Solutions',
    govDesc: 'Comprehensive IT solutions for government departments including digital transformation, e-governance, and citizen services.',
    
    digitalTitle: 'Digital Transformation',
    digitalDesc: 'End-to-end digital transformation services for schools, colleges, and universities.',
    
    erpTitle: 'ERP & Automation',
    erpDesc: 'Institutional ERP systems and process automation for improved efficiency.',
    
    skillTitle: 'Skill Development Programs',
    skillDesc: 'Customized skill development and training programs for students and faculty.',
    
    features: 'Key Features',
    feature1: 'GIGW Compliant Solutions',
    feature2: 'Data Security & Privacy',
    feature3: 'Scalable Architecture',
    feature4: '24/7 Support'
  },
  
  // Careers Page
  careers: {
    pageTitle: 'Careers & Internships',
    breadcrumb: 'Careers',
    intro: 'Join our team and build a rewarding career in technology.',
    
    internTitle: 'Internship Programs',
    internDesc: 'Gain real-world experience through our structured internship programs designed for students.',
    
    trainingTitle: 'Training-Linked Programs',
    trainingDesc: 'Programs that combine learning with practical work experience.',
    
    skillTitle: 'Skill-Based Learning',
    skillDesc: 'Develop in-demand skills through our focused training modules.',
    
    benefits: 'What We Offer',
    benefit1: 'Hands-on project experience',
    benefit2: 'Mentorship from experts',
    benefit3: 'Industry exposure',
    benefit4: 'Certificate of completion',
    benefit5: 'Performance-based opportunities',
    
    applyTitle: 'How to Apply',
    applyDesc: 'Interested candidates can reach out to us through our contact page with their resume and area of interest.'
  },
  
  // Contact Page
  contact: {
    pageTitle: 'Contact Us',
    breadcrumb: 'Contact',
    intro: 'Get in touch with us for inquiries, partnerships, or support.',
    
    companyInfo: 'Company Information',
    companyName: 'Imagenators Global Technology Private Limited',
    cin: 'CIN',
    website: 'Website',
    email: 'Email',
    phone: 'Phone',
    
    formTitle: 'Send us a Message',
    name: 'Full Name',
    emailLabel: 'Email Address',
    subject: 'Subject',
    message: 'Message',
    submit: 'Submit',
    
    subjects: {
      general: 'General Inquiry',
      services: 'Service Inquiry',
      training: 'Training Programs',
      partnership: 'Partnership',
      careers: 'Careers/Internships'
    }
  },
  
  // Footer
  footer: {
    companyName: 'Imagenators Global Technology Pvt. Ltd.',
    tagline: 'Technology • Innovation • Growth',
    quickLinks: 'Quick Links',
    services: 'Services',
    connect: 'Connect With Us',
    copyright: '© 2025 Imagenators Global Technology Private Limited. All rights reserved.',
    cin: 'CIN: U62099UP2025PTC223917'
  },
  
  // Common
  common: {
    readMore: 'Read More',
    viewAll: 'View All',
    getInTouch: 'Get In Touch',
    backToTop: 'Back to Top'
  }
};

// Hindi translations
const hi = {
  // Navigation
  nav: {
    home: 'होम',
    about: 'हमारे बारे में',
    services: 'सेवाएं',
    training: 'प्रशिक्षण कार्यक्रम',
    govEdu: 'सरकार और शिक्षा',
    careers: 'करियर',
    contact: 'संपर्क करें'
  },
  
  // Header
  header: {
    officialWebsite: 'इमेजिनेटर्स ग्लोबल टेक्नोलॉजी प्रा. लि. की आधिकारिक वेबसाइट',
    textSize: 'टेक्स्ट आकार',
    language: 'भाषा'
  },
  
  // Home Page
 home: {
  // Hero
  tagline: 'तकनीक • नवाचार • विकास',
  heroTitle: 'स्मार्ट डिजिटल समाधान एक जुड़े हुए भविष्य के लिए',
  heroSubtitle:
    'हम स्टार्टअप्स, व्यवसायों, संस्थानों और बढ़ते संगठनों के लिए स्केलेबल वेबसाइट्स, एआई-आधारित प्लेटफॉर्म, क्लाउड सिस्टम और एंटरप्राइज़ समाधान विकसित करते हैं।',
  exploreServices: 'हमारे समाधान देखें',
  contactUs: 'संपर्क करें',
  learnMore: 'और जानें',

  // Introduction
  introTitle: 'आपका विश्वसनीय टेक्नोलॉजी पार्टनर',
  introText:
    'इमेजिनेटर्स ग्लोबल टेक्नोलॉजी प्राइवेट लिमिटेड (IGT) एक तकनीक-आधारित कंपनी है जो आधुनिक डिजिटल समाधान, एआई-संचालित सिस्टम और पेशेवर प्रशिक्षण कार्यक्रम प्रदान करती है। हम स्टार्टअप्स, एंटरप्राइज़, शैक्षणिक संस्थानों और संगठनों को उनके विचारों को प्रभावशाली डिजिटल उत्पादों में बदलने में सहायता करते हैं।',

  // Services Overview
  servicesTitle: 'हम क्या बनाते हैं',
  servicesSubtitle:
    'प्रदर्शन, स्केलेबिलिटी और विकास के लिए डिज़ाइन किए गए एंड-टू-एंड डिजिटल समाधान',

  // Training
  trainingTitle: 'प्रशिक्षण एवं कौशल विकास',
  trainingSubtitle:
    'छात्रों, पेशेवरों और संस्थानों के लिए उद्योग-तैयार कौशल',
  trainingDescription:
    'हमारे प्रशिक्षण कार्यक्रम आर्टिफिशियल इंटेलिजेंस, फुल-स्टैक डेवलपमेंट, क्लाउड कंप्यूटिंग और डेटा साइंस जैसी उच्च-मांग वाली तकनीकों में व्यावहारिक, हैंड्स-ऑन सीखने पर केंद्रित हैं।',
  viewPrograms: 'प्रशिक्षण कार्यक्रम देखें',

  // Partners
  partnersTitle: 'हम किनके साथ काम करते हैं',
  partnersSubtitle:
    'स्टार्टअप्स, एंटरप्राइज़, संस्थान और संगठन जो हम पर भरोसा करते हैं',

  // Why Choose
  whyTitle: 'IGT क्यों चुनें?',
  whySubtitle:
    'तकनीकी विशेषज्ञता, नवाचार और विश्वसनीयता का सही संयोजन',

  // CTA
  ctaTitle: 'आइए मिलकर कुछ प्रभावशाली बनाएं',
  ctaSubtitle:
    'विचार से लेकर कार्यान्वयन तक, हम ऐसे डिजिटल समाधान बनाते हैं जो वास्तविक प्रभाव डालते हैं।',
  ctaButton: 'अपना प्रोजेक्ट शुरू करें'
  },

  
  // About Page
  about: {
    pageTitle: 'हमारे बारे में',
    breadcrumb: 'IGT के बारे में',
    
    overviewTitle: 'संगठन अवलोकन',
    overviewText1: 'इमेजिनेटर्स ग्लोबल टेक्नोलॉजी प्राइवेट लिमिटेड (IGT) कंपनी अधिनियम, 2013 के तहत निगमित एक उभरती प्रौद्योगिकी कंपनी है, जिसका CIN: U62099UP2025PTC223917 है।',
    overviewText2: 'हमारा ध्यान सरकारी विभागों, शैक्षणिक संस्थानों और उद्यमों को अत्याधुनिक प्रौद्योगिकी समाधानों के साथ सशक्त बनाने पर है।',
    
    missionTitle: 'हमारा मिशन',
    missionText: 'नवीन प्रौद्योगिकी समाधानों, गुणवत्ता प्रशिक्षण कार्यक्रमों और समर्पित सेवा उत्कृष्टता के माध्यम से सरकार, शिक्षा और उद्यम क्षेत्रों में डिजिटल परिवर्तन को सक्षम करना।',
    
    visionTitle: 'हमारा विजन',
    visionText: 'भारत में संस्थागत डिजिटल परिवर्तन के लिए एक अग्रणी प्रौद्योगिकी भागीदार बनना।',
    
    valuesTitle: 'हमारे मूल मूल्य',
    values: {
      integrity: 'ईमानदारी और पारदर्शिता',
      innovation: 'नवाचार और उत्कृष्टता',
      commitment: 'ग्राहक प्रतिबद्धता',
      quality: 'गुणवत्ता आश्वासन'
    }
  },
  
  // Services Page
  services: {
    pageTitle: 'हमारी सेवाएं',
    breadcrumb: 'सेवाएं',
    intro: 'IGT सरकार, शिक्षा और उद्यम क्षेत्रों की विशिष्ट आवश्यकताओं को पूरा करने के लिए डिज़ाइन की गई आईटी सेवाओं की एक व्यापक श्रृंखला प्रदान करता है।',
    
    itConsulting: 'आईटी सेवाएं और परामर्श',
    itConsultingDesc: 'संगठनों को उनके प्रौद्योगिकी निवेश और डिजिटल पहलों को अनुकूलित करने में मदद करने के लिए रणनीतिक आईटी परामर्श।',
    
    software: 'सॉफ्टवेयर और अनुप्रयोग विकास',
    softwareDesc: 'आपकी विशिष्ट आवश्यकताओं के अनुरूप कस्टम सॉफ्टवेयर विकास, वेब अनुप्रयोग और मोबाइल समाधान।',
    
    cloud: 'क्लाउड और DevOps समाधान',
    cloudDesc: 'स्केलेबल और कुशल संचालन के लिए क्लाउड माइग्रेशन, इंफ्रास्ट्रक्चर प्रबंधन और DevOps कार्यान्वयन।',
    
    digitalMarketing: 'डिजिटल मार्केटिंग समाधान',
    digitalMarketingDesc: 'SEO, सोशल मीडिया प्रबंधन और ऑनलाइन उपस्थिति अनुकूलन सहित व्यापक डिजिटल मार्केटिंग सेवाएं।',
    
    ai: 'कृत्रिम बुद्धिमत्ता समाधान',
    aiDesc: 'मशीन लर्निंग, प्राकृतिक भाषा प्रसंस्करण और बुद्धिमान स्वचालन सहित AI-संचालित समाधान।',
    
    erp: 'ERP समाधान और कार्यान्वयन',
    erpDesc: 'सुव्यवस्थित संचालन और एकीकृत व्यवसाय प्रबंधन के लिए एंटरप्राइज रिसोर्स प्लानिंग समाधान।',
    
    govEnterprise: 'सरकार और उद्यम आईटी',
    govEnterpriseDesc: 'सुरक्षा और अनुपालन पर ध्यान केंद्रित करते हुए सरकारी विभागों और बड़े उद्यमों के लिए विशेष आईटी समाधान।'
  },
  
  // Training Page
  // Training Page
training: {
  pageTitle: 'प्रशिक्षण एवं कार्यक्रम',
  breadcrumb: 'प्रशिक्षण',

  intro: 'IGT स्कूलों, कॉलेजों और संस्थानों के लिए संरचित ऑफलाइन कार्यशालाएं  आयोजित करता है, जिनका उद्देश्य व्यावहारिक तकनीकी अनुभव, नवाचार और कौशल विकास प्रदान करना है।',

  robotics: 'रोबोटिक्स एवं नवाचार कार्यशाला',
  roboticsDesc: ' व्यावहारिक रोबोटिक्स कार्यशाला जिसमें छात्र बेसिक, इंटरमीडिएट और एडवांस स्तर के किट्स का उपयोग करके कार्यशील रोबोट मॉडल बनाते और प्रोग्राम करते हैं।',

  aiTraining: 'एआई एवं उभरती प्रौद्योगिकी कार्यशाला',
  aiTrainingDesc: 'कृत्रिम बुद्धिमत्ता की मूल अवधारणाओं, वास्तविक उपयोग मामलों और डेमो के साथ इंटरैक्टिव कार्यशाला।',

  ml: 'डेटा साइंस आधारभूत कार्यशाला',
  mlDesc: 'डेटा विश्लेषण, विज़ुअलाइज़ेशन और मशीन लर्निंग की प्रारंभिक समझ पर केंद्रित कार्यशाला।',

  python: 'पायथन प्रोग्रामिंग कार्यशाला',
  pythonDesc: 'प्रोग्रामिंग की मूल बातें, तार्किक सोच और बेसिक एप्लिकेशन डेवलपमेंट पर आधारित कार्यशाला।',

  fullstack: 'वेब डेवलपमेंट कार्यशाला',
  fullstackDesc: 'फ्रंटएंड की मूल बातें और बेसिक डिप्लॉयमेंट अवधारणाओं के साथ व्यावहारिक वेब विकास परिचय।',

  cloudTraining: 'क्लाउड एवं आधुनिक इंफ्रास्ट्रक्चर कार्यशाला',
  cloudTrainingDesc: 'क्लाउड प्लेटफॉर्म, इंफ्रास्ट्रक्चर की मूल बातें और आधुनिक डिप्लॉयमेंट प्रथाओं का परिचय।',

  devops: 'DevOps एवं ऑटोमेशन कार्यशाला',
  devopsDesc: 'वर्ज़न कंट्रोल, CI/CD और ऑटोमेशन की मूल अवधारणाओं पर आधारित परिचयात्मक कार्यशाला।',

  duration: 'अवधि',
  days: 'दिन',

  knowMore: 'विस्तार से जानें',

  highlights: 'कार्यशाला की विशेषताएं',
  highlight1: 'हैंड्स-ऑन प्रैक्टिकल प्रशिक्षण',
  highlight2: 'ऑफलाइन इंटरैक्टिव सत्र (प्रतिदिन 4–5 घंटे)',
  highlight3: 'कार्यशाला सहभागिता प्रमाण पत्र',
  highlight4: 'संरचित रोबोटिक्स किट आधारित शिक्षण'
},
  
  // Government & Education Page
  govEdu: {
    pageTitle: 'सरकार और शिक्षा समाधान',
    breadcrumb: 'सरकार और शिक्षा',
    intro: 'सरकारी विभागों और शैक्षणिक संस्थानों के लिए डिज़ाइन किए गए विशेष प्रौद्योगिकी समाधान।',
    
    govTitle: 'सरकारी आईटी समाधान',
    govDesc: 'डिजिटल परिवर्तन, ई-गवर्नेंस और नागरिक सेवाओं सहित सरकारी विभागों के लिए व्यापक आईटी समाधान।',
    
    digitalTitle: 'डिजिटल परिवर्तन',
    digitalDesc: 'स्कूलों, कॉलेजों और विश्वविद्यालयों के लिए एंड-टू-एंड डिजिटल परिवर्तन सेवाएं।',
    
    erpTitle: 'ERP और ऑटोमेशन',
    erpDesc: 'बेहतर दक्षता के लिए संस्थागत ERP सिस्टम और प्रक्रिया स्वचालन।',
    
    skillTitle: 'कौशल विकास कार्यक्रम',
    skillDesc: 'छात्रों और संकाय के लिए अनुकूलित कौशल विकास और प्रशिक्षण कार्यक्रम।',
    
    features: 'मुख्य विशेषताएं',
    feature1: 'GIGW अनुपालन समाधान',
    feature2: 'डेटा सुरक्षा और गोपनीयता',
    feature3: 'स्केलेबल आर्किटेक्चर',
    feature4: '24/7 सहायता'
  },
  
  // Careers Page
  careers: {
    pageTitle: 'करियर और इंटर्नशिप',
    breadcrumb: 'करियर',
    intro: 'हमारी टीम से जुड़ें और प्रौद्योगिकी में एक पुरस्कृत करियर बनाएं।',
    
    internTitle: 'इंटर्नशिप कार्यक्रम',
    internDesc: 'छात्रों के लिए डिज़ाइन किए गए हमारे संरचित इंटर्नशिप कार्यक्रमों के माध्यम से वास्तविक दुनिया का अनुभव प्राप्त करें।',
    
    trainingTitle: 'प्रशिक्षण-लिंक्ड कार्यक्रम',
    trainingDesc: 'ऐसे कार्यक्रम जो सीखने को व्यावहारिक कार्य अनुभव के साथ जोड़ते हैं।',
    
    skillTitle: 'कौशल-आधारित शिक्षा',
    skillDesc: 'हमारे केंद्रित प्रशिक्षण मॉड्यूल के माध्यम से मांग में कौशल विकसित करें।',
    
    benefits: 'हम क्या प्रदान करते हैं',
    benefit1: 'प्रोजेक्ट पर हैंड्स-ऑन अनुभव',
    benefit2: 'विशेषज्ञों से मार्गदर्शन',
    benefit3: 'उद्योग एक्सपोजर',
    benefit4: 'पूर्णता का प्रमाण पत्र',
    benefit5: 'प्रदर्शन-आधारित अवसर',
    
    applyTitle: 'आवेदन कैसे करें',
    applyDesc: 'इच्छुक उम्मीदवार अपने रिज्यूमे और रुचि के क्षेत्र के साथ हमारे संपर्क पृष्ठ के माध्यम से हमसे संपर्क कर सकते हैं।'
  },
  
  // Contact Page
  contact: {
    pageTitle: 'संपर्क करें',
    breadcrumb: 'संपर्क',
    intro: 'पूछताछ, साझेदारी या सहायता के लिए हमसे संपर्क करें।',
    
    companyInfo: 'कंपनी की जानकारी',
    companyName: 'इमेजिनेटर्स ग्लोबल टेक्नोलॉजी प्राइवेट लिमिटेड',
    cin: 'CIN',
    website: 'वेबसाइट',
    email: 'ईमेल',
    phone: 'फोन',
    
    formTitle: 'हमें संदेश भेजें',
    name: 'पूरा नाम',
    emailLabel: 'ईमेल पता',
    subject: 'विषय',
    message: 'संदेश',
    submit: 'सबमिट करें',
    
    subjects: {
      general: 'सामान्य पूछताछ',
      services: 'सेवा पूछताछ',
      training: 'प्रशिक्षण कार्यक्रम',
      partnership: 'साझेदारी',
      careers: 'करियर/इंटर्नशिप'
    }
  },
  
  // Footer
  footer: {
    companyName: 'इमेजिनेटर्स ग्लोबल टेक्नोलॉजी प्रा. लि.',
    tagline: 'तकनीक • नवाचार • विकास',
    quickLinks: 'त्वरित लिंक',
    services: 'सेवाएं',
    connect: 'हमसे जुड़ें',
    copyright: '© 2025 इमेजिनेटर्स ग्लोबल टेक्नोलॉजी प्राइवेट लिमिटेड। सर्वाधिकार सुरक्षित।',
    cin: 'CIN: U62099UP2025PTC223917'
  },
  
  // Common
  common: {
    readMore: 'और पढ़ें',
    viewAll: 'सभी देखें',
    getInTouch: 'संपर्क करें',
    backToTop: 'ऊपर जाएं'
  }
};

const translations = { en, hi };

export function LanguageProvider({ children }) {
  const [language, setLanguage] = useState('en');

  const toggleLanguage = useCallback((lang) => {
    setLanguage(lang);
    document.documentElement.lang = lang;
  }, []);

  const t = useCallback((path) => {
    const keys = path.split('.');
    let value = translations[language];
    for (const key of keys) {
      value = value?.[key];
    }
    return value || path;
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
